import React from 'react'
import { setCacheHeaders } from '@scentregroup/shared/helpers/set-cache-headers'
import { useGetHrefContext } from '@scentregroup/shared/context'
import { getDefaultCentreMetadata } from '@scentregroup/shared/helpers/get-default-centre-metadata'
import {
  CentreStaticData,
  fetchCentreStaticData,
} from '@scentregroup/shared/hub-components/universal-header'
import { localeToCountry } from '@scentregroup/shared/helpers/local-to-country'
import Layout from '@scentregroup/shared/hub-components/layout'
import {
  getCentreDetails,
  CentreDetails,
  buildContactLinks,
} from '@scentregroup/shared/helpers/centre'
import { useStoredCentre } from '@scentregroup/shared/hooks/use-stored-centre'
import { SGLinkedData } from '@scentregroup/shared/hub-components/json-ld'
import {
  GetServerSidePropsResult,
  GetServerSidePropsContext,
} from '../../types/next'
import { makeApolloClient } from '../../lib/apolloClient'
import {
  getCentrePageTemplate,
  getCentreQuickLinks,
} from '../../lib/page-templates/fetching'
import {
  SGPageTemplateWithAdZones,
  SGPageTemplateContentCollection,
} from '../../../../src/types/page-templates'
import { OpenGraph } from '../../lib/open-graph'
import {
  getTopStripProps,
  ITopStripProps,
} from '@scentregroup/shared/hub-components/top-strip'
import { Template } from '@scentregroup/shared/components/page-templates/template'
import UtilityPanelModule from '../../components/page-templates/modules/content-collection/utility-panel-module'
import { getCentreTradingHoursStartingNow } from '../../lib/get-trading-hours'
import { TradingHourDay } from '@scentregroup/shared/graphql'
import { CentreFrontInfo } from '../../components/pages/centre/centrefront-info'
import useFeatureFlag from '@scentregroup/shared/hooks/use-feature-flag'
import { FeatureFlagName } from '@scentregroup/shared/feature-flags'
import { shouldRedirectForMismatchedRoute } from '@scentregroup/shared/lib/redirects'

const TRADING_HOUR_DAYS = 7

export async function getServerSideProps(
  ctx: GetServerSidePropsContext<{
    centre: string
  }>
): Promise<GetServerSidePropsResult<ICentrePageProps>> {
  setCacheHeaders(ctx.res)
  const country = localeToCountry(ctx.locale)
  const centreSlug = ctx.params?.centre
  if (!centreSlug) {
    return {
      notFound: true,
    }
  }

  const apollo = makeApolloClient()

  const centre = await getCentreDetails(apollo, { slug: centreSlug, country })
  if (!centre) {
    return {
      notFound: true,
    }
  }

  const redirectForCentreSlug = shouldRedirectForMismatchedRoute(
    '/[centre]',
    ctx.params,
    {
      centre: centre.slug,
    }
  )
  if (redirectForCentreSlug) {
    return redirectForCentreSlug
  }

  const [
    template,
    headerStaticData,
    topStripProps,
    tradingHours,
    quickLinksModule,
  ] = await Promise.all([
    getCentrePageTemplate(apollo, centreSlug, 'centre'),
    fetchCentreStaticData(apollo, {
      country,
      centre,
    }),
    getTopStripProps(apollo, country, centreSlug),
    getCentreTradingHoursStartingNow(apollo, centre, TRADING_HOUR_DAYS),
    getCentreQuickLinks(apollo, centreSlug),
  ])
  if (!template) {
    return {
      notFound: true,
    }
  }

  return {
    props: {
      country,
      topStripProps,
      centre,
      template,
      quickLinksModule,
      headerStaticData,
      tradingHours,
    },
  }
}

export interface ICentrePageProps {
  country: ReturnType<typeof localeToCountry>
  topStripProps: ITopStripProps | null
  centre: CentreDetails
  template: SGPageTemplateWithAdZones
  quickLinksModule: SGPageTemplateContentCollection | null
  headerStaticData: CentreStaticData
  isBasicPage?: boolean
  tradingHours: TradingHourDay[]
}

export default function CentrePage({
  country,
  topStripProps,
  centre,
  template,
  quickLinksModule,
  headerStaticData,
  isBasicPage,
  tradingHours,
}: ICentrePageProps): JSX.Element {
  const canonical = useGetHrefContext()()
  const meta = getDefaultCentreMetadata({
    centre,
    canonical,
  })

  const showCentreInfo = useFeatureFlag(
    FeatureFlagName.NEXT_PUBLIC_FEAT_FLAG_CENTRE_HOMEPAGE_HOURS
  )

  const metaData = {
    title: meta.title,
    description: meta.description,
    canonical,
  }

  const contactLinks = buildContactLinks(centre)
  useStoredCentre(centre)
  const centreWithLinks = {
    ...centre,
    contactLinks,
  }
  const hasBottomMarginMobile = React.useMemo(() => {
    const firstModule = template?.modules[0]
    if (!firstModule) {
      return false
    }
    if (firstModule.__typename !== 'SGPageTemplateContentCollection') {
      return false
    }
    if (firstModule.kind !== 'UTILITY_PANEL') {
      return false
    }
    return true
  }, [template?.modules])

  const centreTemplate = React.useMemo(() => {
    if (!showCentreInfo) {
      return template
    }
    return {
      ...template,
      modules: template.modules.filter(
        module =>
          !(
            module.__typename === 'SGPageTemplateContentCollection' &&
            module.kind === 'UTILITY_PANEL'
          )
      ),
    }
  }, [showCentreInfo, template])

  return (
    <>
      <SGLinkedData item={meta.schema} />
      <OpenGraph data={meta.openGraphData} />
      <Layout
        country={country}
        topStrip={topStripProps}
        centre={centreWithLinks}
        header={
          isBasicPage
            ? null
            : {
                variant: 'centre',
                data: headerStaticData,
                hasBottomMargin: [
                  hasBottomMarginMobile,
                  hasBottomMarginMobile,
                  true,
                ],
                logoText: centre.title,
              }
        }
        metaData={metaData}
      >
        <Template template={centreTemplate} activateH1Title={false}>
          {showCentreInfo
            ? [
                ...(quickLinksModule
                  ? [
                      <UtilityPanelModule
                        key="quickLinks"
                        module={quickLinksModule}
                      />,
                    ]
                  : []),
                <CentreFrontInfo
                  key="centre"
                  centre={centre}
                  tradingHours={tradingHours}
                />,
              ]
            : []}
        </Template>
      </Layout>
    </>
  )
}
